import { useState } from "react";
import { FirstAid } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";

export const HospitalizationDialog = ({ clientId, onReported }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/hospitalization`, { date, details });
      toast.success("Hospitalization reported — this client is now flagged for review");
      setOpen(false);
      setDetails("");
      onReported?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-[#C0392B] hover:text-white" data-testid="report-hospitalization-button">
          <FirstAid size={16} className="mr-1.5" /> Report Hospitalization
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="hospitalization-dialog">
        <DialogHeader>
          <DialogTitle>Report hospitalization</DialogTitle>
          <DialogDescription>
            A hospitalization always prompts a care plan review. The dashboard flag clears automatically once a reassessment (Form 2) or approval (Form 5) is completed after this date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Date of hospitalization / ER visit</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border-stone-300 bg-white" data-testid="hosp-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Details (optional)</Label>
            <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Where, why, expected discharge, anything changing about care needs..."
              className="border-stone-300 bg-white" data-testid="hosp-details" />
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={busy || !date} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="hosp-submit">
              {busy ? "Reporting..." : "Report & Flag for Review"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
